import React, { useState } from 'react'
import { useSearchStore } from '../store/searchStore'
import { FiDownload, FiFileText, FiCheck } from 'react-icons/fi'
import api from '../config/axios'
import QuoteModal from './QuoteModal'
import DrawingModal from './DrawingModal'

const MANUFACTURER_LOGOS = {
  'Pelican Products': { src: '/logo-pelican.png', filter: 'brightness(0) invert(1)', height: '16px', maxWidth: '80px' },
  'GT Line Explorer': { src: '/logo-explorer.png', filter: 'none',                   height: '20px', maxWidth: '90px' },
  'Suprobox':         { src: '/logo-suprobox.png', filter: 'none',                   height: '26px', maxWidth: '110px' },
}

function ManufacturerLogo({ manufacturer }) {
  const logo = MANUFACTURER_LOGOS[manufacturer]
  if (!logo) return null
  return (
    <img
      src={logo.src}
      alt={manufacturer}
      className="object-contain"
      style={{ height: logo.height, maxWidth: logo.maxWidth, filter: logo.filter }}
    />
  )
}

const orientationColors = [
  { bg: 'bg-blue-900/30', border: 'border-blue-600', text: 'text-blue-300' },
  { bg: 'bg-purple-900/30', border: 'border-purple-600', text: 'text-purple-300' },
  { bg: 'bg-pink-900/30', border: 'border-pink-600', text: 'text-pink-300' },
  { bg: 'bg-indigo-900/30', border: 'border-indigo-600', text: 'text-indigo-300' },
  { bg: 'bg-cyan-900/30', border: 'border-cyan-600', text: 'text-cyan-300' },
  { bg: 'bg-emerald-900/30', border: 'border-emerald-600', text: 'text-emerald-300' }
]

function SearchResults() {
  const { results, loading, searchConfig } = useSearchStore()
  const [exporting, setExporting] = useState(false)
  const [quoteTarget, setQuoteTarget] = useState(null) // result object for modal
  const [quotedModels, setQuotedModels] = useState(new Set()) // models already requested
  const [drawingTarget, setDrawingTarget] = useState(null) // result object for drawing modal
  const [drawnModels, setDrawnModels] = useState(new Set()) // models already requested for drawing

  const handleQuoteSuccess = (model) => {
    setQuotedModels(prev => new Set([...prev, model]))
  }

  const handleDrawingSuccess = (model) => {
    setDrawnModels(prev => new Set([...prev, model]))
  }

  if (loading) {
    return (
      <div className="card flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-yellow"></div>
      </div>
    )
  }

  if (results.length === 0) {
    return null
  }

  const handleExportResults = async () => {
    setExporting(true)
    try {
      const { jsPDF } = await import('jspdf')
      const { autoTable } = await import('jspdf-autotable')

      const today = new Date().toISOString().split('T')[0]
      const toleranceLW = searchConfig?.toleranceLW ?? 3
      const toleranceD = searchConfig?.toleranceD ?? 1.5
      const fmt = v => String(parseFloat(parseFloat(v).toFixed(2)))
      const PAGE_W = 297, PAGE_H = 210, MARGIN = 14, USABLE = PAGE_W - MARGIN * 2

      // Fetch image, normalise via canvas to avoid jsPDF PNG compatibility issues
      const toBase64 = (url) => new Promise(resolve => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            canvas.width = img.naturalWidth
            canvas.height = img.naturalHeight
            const ctx = canvas.getContext('2d')
            ctx.fillStyle = '#ffffff'
            ctx.fillRect(0, 0, canvas.width, canvas.height)
            ctx.drawImage(img, 0, 0)
            resolve(canvas.toDataURL('image/jpeg', 0.92))
          } catch { resolve(null) }
        }
        img.onerror = () => resolve(null)
        img.src = url
      })

      // Pre-fetch all product images + logos
      const allModels = [...new Set(results.map(r => r.model))]
      const imageMap = {}
      await Promise.all(allModels.map(async model => {
        const sanitizedModel = model.replace(/\//g, '_')
        imageMap[model] = await toBase64(`/cases-images/${sanitizedModel}.png`)
      }))
      const activeManufacturers = [...new Set(results.map(r => r.manufacturer))]
      const logoPelican  = activeManufacturers.includes('Pelican Products') ? await toBase64('/logo-pelican.png')  : null
      const logoExplorer = activeManufacturers.includes('GT Line Explorer') ? await toBase64('/logo-explorer.png') : null
      const logoSuprobox = activeManufacturers.includes('Suprobox')         ? await toBase64('/logo-suprobox 1.png') : null

      const orientationNames = [
        'Original', 'Swap W and D', 'Swap L and W',
        'Rotate: L->D->W->L', 'Rotate: L->W->D->L', 'All reversed'
      ]

      const bestFit = results.reduce((best, r) =>
        r.volumeRatio != null && (best === null || r.volumeRatio < best.volumeRatio) ? r : best
      , null)

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })

      // ── Header ──
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(40, 40, 40)
      doc.text('Case Sizing Engine - Search Results', MARGIN, 13)

      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(120)
      doc.text(`Generated: ${today}`, MARGIN, 19)

      // Logos top-right
      let logoX = PAGE_W - MARGIN
      if (logoPelican) {
        logoX -= 28
        try { doc.addImage(logoPelican, 'PNG', logoX, 6, 28, 10) } catch {}
        logoX -= 4
      }
      if (logoExplorer) {
        logoX -= 30
        try { doc.addImage(logoExplorer, 'PNG', logoX, 6, 30, 10) } catch {}
        logoX -= 4
      }
      if (logoSuprobox) {
        logoX -= 28
        try { doc.addImage(logoSuprobox, 'PNG', logoX, 6, 28, 9) } catch {}
      }

      // Best fit line (bold, amber)
      let startY = 26
      if (bestFit) {
        doc.setFontSize(9)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(180, 120, 10)
        doc.text(
          `Best Overall Fit: ${bestFit.model} (${bestFit.series})  -  L ${fmt(bestFit.length)} x W ${fmt(bestFit.width)} x D ${fmt(bestFit.depth)} cm  -  Volume ratio: ${bestFit.volumeRatio}x`,
          MARGIN, startY
        )
        startY += 7
      }

      // ── Groups ──
      const groups = []
      const seen = {}
      results.forEach(r => {
        if (!seen[r.orientation]) { seen[r.orientation] = true; groups.push({ key: r.orientation, rows: [] }) }
        groups[groups.length - 1].rows.push(r)
      })

      const IMG_W = 38, IMG_H = 32, IMG_GAP = 4, IMG_LABEL_H = 10

      for (let gi = 0; gi < groups.length; gi++) {
        const group = groups[gi]
        const f = group.rows[0]?.fittedAs
        const actualOrientIdx = group.rows[0]?.orientationIndex ?? gi
        const orientLabel = f ? `L ${fmt(f.l)} x W ${fmt(f.w)} x D ${fmt(f.d)} cm` : group.key
        const title = `${orientationNames[actualOrientIdx] ?? `Orientation ${actualOrientIdx + 1}`}: ${orientLabel}`

        // Check if table + images fit on current page; add new page if not
        const estTableH = 14 + group.rows.length * 6
        const estBlockH = estTableH + IMG_H + IMG_LABEL_H + 8
        if (startY + estBlockH > PAGE_H - 16) {
          doc.addPage()
          startY = 14
        }

        const orientPart = `${orientationNames[actualOrientIdx] ?? `Orientation ${actualOrientIdx + 1}`}:`
        const dimPart = `          ${orientLabel}          |          All dimensions for cases are internal`

        autoTable(doc, {
          startY,
          head: [
            [{ content: orientPart + dimPart, colSpan: 9, styles: { fillColor: [30, 41, 59], textColor: [148, 226, 213], fontStyle: 'bold', fontSize: 8 } }],
            [
              { content: 'Rank',       styles: { fontStyle: 'bold' } },
              { content: 'Model',      styles: { fontStyle: 'bold' } },
              { content: 'Series',     styles: { fontStyle: 'bold' } },
              { content: 'L (cm)',     styles: { fontStyle: 'bold' } },
              { content: 'W (cm)',     styles: { fontStyle: 'bold' } },
              { content: 'D (cm)',     styles: { fontStyle: 'bold' } },
              { content: 'Lid D (cm)', styles: { fontStyle: 'bold' } },
              { content: 'Base D (cm)',styles: { fontStyle: 'bold' } },
              { content: 'Vol. Ratio', styles: { fontStyle: 'bold' } },
            ]
          ],
          body: group.rows.map(r => [
            String(r.rank), String(r.model), String(r.series),
            fmt(r.length), fmt(r.width), fmt(r.depth),
            fmt(r.lidDepth), fmt(r.baseDepth), String(r.volumeRatio)
          ]),
          headStyles: { fillColor: [15, 23, 42], textColor: [203, 213, 225], fontSize: 7, fontStyle: 'bold' },
          bodyStyles: { fontSize: 7, textColor: [50, 50, 50], fillColor: [245, 245, 245] },
          alternateRowStyles: { fillColor: [230, 235, 240] },
          columnStyles: {
            0: { cellWidth: 10 }, 1: { cellWidth: 30 }, 2: { cellWidth: 28 },
            3: { cellWidth: 22 }, 4: { cellWidth: 22 }, 5: { cellWidth: 22 },
            6: { cellWidth: 24 }, 7: { cellWidth: 25 }, 8: { cellWidth: 18 }
          },
          margin: { left: MARGIN, right: MARGIN },
          theme: 'grid',
        })

        // ── Product images row ──
        let imgY = doc.lastAutoTable.finalY + 4
        if (imgY + IMG_H + IMG_LABEL_H > PAGE_H - 14) {
          doc.addPage()
          imgY = 14
        }

        group.rows.slice(0, 6).forEach((r, idx) => {
          const imgX = MARGIN + idx * (IMG_W + IMG_GAP)
          const b64 = imageMap[r.model]
          if (b64) {
            try { doc.addImage(b64, 'JPEG', imgX, imgY, IMG_W, IMG_H) } catch {}
          } else {
            doc.setDrawColor(180)
            doc.rect(imgX, imgY, IMG_W, IMG_H)
          }
          // Model label
          doc.setFontSize(6.5)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(40)
          doc.text(String(r.model), imgX + IMG_W / 2, imgY + IMG_H + 4, { align: 'center', maxWidth: IMG_W })
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(100)
          doc.text(`L ${fmt(r.length)} x W ${fmt(r.width)} x D ${fmt(r.depth)}`, imgX + IMG_W / 2, imgY + IMG_H + 8, { align: 'center', maxWidth: IMG_W })
        })

        startY = imgY + IMG_H + IMG_LABEL_H + 6
      }

      // ── Footer note ──
      const noteY = Math.min(startY + 2, PAGE_H - 10)
      if (noteY < PAGE_H - 6) {
        doc.setFontSize(6.5)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(130)
        const note = `Note: Dimensions padded for foam/insert clearance - L & W +${toleranceLW} cm, D +${toleranceD} cm. Suggestions only; final decision is the sole responsibility of the user.`
        doc.text(doc.splitTextToSize(note, USABLE), MARGIN, noteY)
      }

      doc.save(`Case Sizing Engine ${today}.pdf`)
    } catch (err) {
      console.error('Export error:', err)
      if (err?.message?.includes('Failed to fetch dynamically imported module') || err?.message?.includes('Importing a module script failed')) {
        if (confirm('The app has been updated. Reload the page to apply the update, then try again.')) {
          window.location.reload()
        }
      } else {
        alert(`PDF export failed: ${err.message}`)
      }
    } finally {
      setExporting(false)
    }
  }

  // Best overall fit — lowest volume ratio across all results
  const bestFit = results.reduce((best, r) =>
    r.volumeRatio != null && (best === null || r.volumeRatio < best.volumeRatio) ? r : best
  , null)

  // Group results by orientation (insertion order preserved)
  const groupedByOrientation = {}
  results.forEach((result) => {
    if (!groupedByOrientation[result.orientation]) {
      groupedByOrientation[result.orientation] = []
    }
    groupedByOrientation[result.orientation].push(result)
  })

  return (
    <div className="space-y-6">
      {/* Quote request modal */}
      {quoteTarget && (
        <QuoteModal
          result={quoteTarget}
          onClose={() => setQuoteTarget(null)}
          onSuccess={handleQuoteSuccess}
        />
      )}

      {/* Drawing request modal */}
      {drawingTarget && (
        <DrawingModal
          result={drawingTarget}
          onClose={() => setDrawingTarget(null)}
          onSuccess={handleDrawingSuccess}
        />
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-accent-yellow">
          Found {results.length} Matching Case{results.length !== 1 ? 's' : ''}
        </h2>
        <button
          onClick={handleExportResults}
          disabled={exporting}
          className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiDownload size={18} />
          {exporting ? 'Generating...' : 'Export PDF'}
        </button>
      </div>

      {/* Best overall fit banner */}
      {bestFit && (
        <div className="p-4 bg-bg-card border-2 border-accent-yellow rounded-lg">
          <p className="text-xs font-semibold text-accent-yellow uppercase tracking-widest mb-1">Best Overall Fit</p>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-16 h-16 bg-bg-card rounded flex-shrink-0 flex items-center justify-center">
              <img
                src={`/cases-images/${bestFit.model.replace(/\//g, '_')}.png`}
                alt={bestFit.model}
                className="w-full h-full object-contain rounded"
                onError={e => { e.currentTarget.parentElement.style.display = 'none' }}
              />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xl font-bold text-accent-teal">{bestFit.model}</p>
                <ManufacturerLogo manufacturer={bestFit.manufacturer} />
              </div>
              <p className="text-sm text-gray-400">{bestFit.series}</p>
              <p className="text-sm text-gray-300 mt-0.5">
                L {parseFloat(bestFit.length.toFixed(2))} × W {parseFloat(bestFit.width.toFixed(2))} × D {parseFloat(bestFit.depth.toFixed(2))} cm
              </p>
              {(bestFit.baseDepth || bestFit.lidDepth) && (
                <p className="text-xs text-gray-400 mt-0.5">
                  Base {parseFloat((bestFit.baseDepth ?? 0).toFixed(2))} cm · Lid {parseFloat((bestFit.lidDepth ?? 0).toFixed(2))} cm
                </p>
              )}
            </div>
            <div className="ml-auto flex items-center gap-3 sm:flex-col sm:items-end sm:gap-2">
              <div className="sm:text-right">
                <p className="text-xs text-gray-400">Volume ratio</p>
                <p className="text-2xl font-bold text-accent-yellow">{bestFit.volumeRatio}×</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {['Original', 'Swap W and D', 'Swap L and W', 'Rotate: L→D→W→L', 'Rotate: L→W→D→L', 'All reversed'][bestFit.orientationIndex] ?? `Orientation ${bestFit.orientationIndex + 1}`}
                </p>
              </div>
              <div className="flex flex-col gap-1.5">
                {quotedModels.has(bestFit.model) ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold bg-accent-teal/20 text-accent-teal border border-accent-teal/40">
                    <FiCheck size={13} /> Quote Requested
                  </span>
                ) : (
                  <button
                    onClick={() => setQuoteTarget(bestFit)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold bg-accent-yellow text-bg-dark hover:bg-[#c96f02] active:bg-[#c96f02] transition"
                  >
                    <FiFileText size={13} /> Request Quote
                  </button>
                )}
                {drawnModels.has(bestFit.model) ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold bg-accent-teal/20 text-accent-teal border border-accent-teal/40">
                    <FiCheck size={13} /> Drawing Requested
                  </span>
                ) : (
                  <button
                    onClick={() => setDrawingTarget(bestFit)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold bg-[#FFB347] text-bg-dark hover:bg-[#FFA020] active:bg-[#FFA020] transition"
                  >
                    <FiFileText size={13} /> Request Drawing
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results grouped by orientation with colors */}
      {Object.entries(groupedByOrientation).map(([orientation, orientationResults], groupIdx) => {
        const colors = orientationColors[groupIdx % orientationColors.length]
        const borderColor = ['#3b82f6', '#a855f7', '#ec4899', '#6366f1', '#06b6d4', '#10b981'][groupIdx % 6]

        return (
          <div key={orientation} className="space-y-3">
            <div className={`p-3 ${colors.bg} border ${colors.border} rounded`}>
              <h3 className={`font-bold text-lg ${colors.text}`}>
                {['Original', 'Swap W and D', 'Swap L and W', 'Rotate: L→D→W→L', 'Rotate: L→W→D→L', 'All reversed'][orientationResults[0]?.orientationIndex ?? groupIdx] ?? `Orientation ${(orientationResults[0]?.orientationIndex ?? groupIdx) + 1}`}
              </h3>
              {orientationResults[0]?.fittedAs && (() => {
                const f = orientationResults[0].fittedAs
                return (
                  <p className="text-xs text-gray-400 mt-1">
                    Your item fitted as: <span className="text-gray-200">L {f.l}cm × W {f.w}cm × D {f.d}cm</span>
                  </p>
                )
              })()}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {orientationResults.map((result, idx) => (
                <div
                  key={idx}
                  className="card !p-3 transition-colors"
                  style={{
                    borderColor: borderColor
                  }}

                >
                  <div className="space-y-3">
                    {/* Header with rank */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 min-w-0">
                          <img
                            src={`/cases-images/${result.model.replace(/\//g, '_')}.png`}
                            alt={result.model}
                            className="w-16 h-16 object-contain rounded bg-bg-card flex-shrink-0"
                            onError={e => { e.currentTarget.style.display = 'none' }}
                          />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-sm font-bold px-2 py-1 rounded ${colors.bg} ${colors.text} flex-shrink-0`}>
                                Rank #{result.rank}
                              </span>
                              <h3 className="text-base font-bold text-accent-teal truncate">{result.model}</h3>
                            </div>
                            <div className="mt-1.5 mb-1">
                              <ManufacturerLogo manufacturer={result.manufacturer} />
                            </div>
                            <p className="text-sm text-gray-400">{result.series}</p>
                          </div>
                        </div>
                      </div>
                      {/* Request Quote + Request Drawing — full width on mobile */}
                      <div className="flex flex-row gap-1.5 w-full sm:w-auto sm:self-end">
                        {quotedModels.has(result.model) ? (
                          <span className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-semibold bg-accent-teal/20 text-accent-teal border border-accent-teal/40 flex-1 sm:flex-none">
                            <FiCheck size={11} /> Quote Requested
                          </span>
                        ) : (
                          <button
                            onClick={() => setQuoteTarget(result)}
                            className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-semibold bg-accent-yellow text-bg-dark hover:bg-[#c96f02] active:bg-[#c96f02] transition flex-1 sm:flex-none"
                          >
                            <FiFileText size={11} /> Request Quote
                          </button>
                        )}
                        {drawnModels.has(result.model) ? (
                          <span className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-semibold bg-accent-teal/20 text-accent-teal border border-accent-teal/40 w-full sm:w-auto">
                            <FiCheck size={11} /> Drawing Requested
                          </span>
                        ) : (
                          <button
                            onClick={() => setDrawingTarget(result)}
                            className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-semibold bg-[#FFB347] text-bg-dark hover:bg-[#FFA020] active:bg-[#FFA020] transition flex-1 sm:flex-none"
                          >
                            <FiFileText size={11} /> Request Drawing
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Dimensions */}
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="bg-bg-dark p-1 rounded">
                        <p className="text-gray-400">Length</p>
                        <p className="font-semibold text-white">{parseFloat(result.length.toFixed(2))} cm</p>
                      </div>
                      <div className="bg-bg-dark p-1 rounded">
                        <p className="text-gray-400">Width</p>
                        <p className="font-semibold text-white">{parseFloat(result.width.toFixed(2))} cm</p>
                      </div>
                      <div className="bg-bg-dark p-1 rounded">
                        <p className="text-gray-400">Depth</p>
                        <p className="font-semibold text-white">{parseFloat(result.depth.toFixed(2))} cm</p>
                        {(result.lidDepth > 0 || result.baseDepth > 0) && (
                          <div className="mt-1 pt-1 border-t border-border-dark space-y-0.5">
                            {result.lidDepth > 0 && (
                              <p className="text-xs text-gray-500">Lid: {parseFloat(result.lidDepth.toFixed(2))} cm</p>
                            )}
                            {result.baseDepth > 0 && (
                              <p className="text-xs text-gray-500">Base: {parseFloat(result.baseDepth.toFixed(2))} cm</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default SearchResults
